import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import Logo from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { GoogleLogo, EnvelopeSimple, Lock } from "@phosphor-icons/react";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      const target = next || (u.role === "tradesperson" ? "/dashboard/trader" : "/dashboard/customer");
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="flex justify-center mb-6"><Logo size={100} /></div>
        <div className="brand-card p-7">
          <h1 className="font-display font-extrabold text-2xl text-center" style={{ color: "var(--brand-green)" }}>Welcome back</h1>
          <p className="text-center text-sm text-[var(--text-muted)] mt-1">Log in to your MyHandyHub account.</p>

          <button
            type="button"
            onClick={handleGoogle}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md border border-[var(--border-stone)] bg-white hover:bg-[#FAF8F2] transition-colors font-semibold"
            data-testid="login-google-btn"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </button>

          <div className="my-5 flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <div className="flex-1 h-px bg-[var(--border-stone)]" /> or <div className="flex-1 h-px bg-[var(--border-stone)]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="text-xs font-semibold text-[var(--text-muted)]">Email</span>
              <div className="mt-1 relative">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                  placeholder="you@example.com"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[var(--text-muted)]">Password</span>
              <div className="mt-1 relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
                  placeholder="Your password"
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {busy ? "Logging in…" : "Log in"}
            </button>
          </form>

          <div className="mt-5 flex justify-between text-sm">
            <Link to="/forgot-password" className="text-[var(--brand-green)] hover:underline" data-testid="forgot-link">Forgot password?</Link>
            <Link to="/register" className="text-[var(--brand-green)] hover:underline" data-testid="register-link">Create account</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
